import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import BodyClassName from 'react-body-classname'
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons"

import Loading from 'components/Loading'
import Input from 'components/Input'
import Toggle from 'components/Toggle'
import ButtonPrimary from 'components/ButtonPrimary'
import PasswordChecklist from './PasswordChecklist'

import { isValid, discectError } from 'lib/utils'
import api from 'lib/api'

import styles from './styles.module.scss'

const PasswordReset = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isTokenValid, setIsTokenValid] = useState(false)
  const [info, setInfo] = useState({})
  const [allRulesMatch, setAllRulesMatch] = useState(false) 

  const navigate = useNavigate()
  const { token } = useParams()

  useEffect(() => {
    const _fetch = async () => {
      setIsLoading(true)
      try {
        const { success } = await api.employees.fetchEmployeeByToken(token)
        if (success) setIsTokenValid(true)
      } catch(e) {
        setError(discectError(e))
      }
      setIsLoading(false)
    }
    _fetch()
  }, [token])

  const _onSave = async () => {
    setError(null)
    try {
      if (isValid(info, ['password','passwordVerify']) && info.isAcceptingTerms) {
        if (allRulesMatch) {
          if (info.password === info.passwordVerify) {
            setIsLoading(true)
            const { success } = await api.employees.resetPassword(token, { password: info.password })
            if (success) {
              navigate(`/callback`)
            } else {
              throw new Error('Login was invalid')
            }
          } else {
            throw new Error('Your passwords do not match.')
          }
        } else {
          throw new Error('You have entered an invalid password')
        }
      } else {
        throw new Error('Please make sure to update all required fields.')
      }
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }

  const _onChange = (id, val) => setInfo(i => { return { ...i, [id]: val } })

  const _isDisabled = () => {
    if (isValid(info, ['password','passwordVerify']) && allRulesMatch && info.isAcceptingTerms) {
      if (info.password === info.passwordVerify) {
        return false
      }
    }
    return true
  }

  return (
    <BodyClassName className={styles.onBoardingPage__template}>
      <div className={styles.onBoardingPage__container}>
        {isTokenValid && (
          <div className={styles.onBoardingPage}>
            <div className={styles.onBoardingPage__poweredBy}>
              <img src="/images/SimplyBail_logo_long.png" alt="Simply Bail" />
            </div>
            <div className={styles.onBoardingPage__header}>
              <h3>Password Reset!</h3>
              <p>To get started enter the password you would like to use, and press Submit.</p>
            </div>
            {error && <p className="error">{error}</p>}
            <div className={styles.onBoardingPage__form}>
              <Input type="password" id="password" label="Password" value={info?.password || ''} onChange={_onChange} isRequired autoFocus />
              <Input type="password" id="passwordVerify" label="Password Verify" value={info?.passwordVerify || ''} onChange={_onChange} isRequired />
            </div>
            <PasswordChecklist
              value={info?.password}
              passwordsMatch={(info?.password === info?.passwordVerify)}
              onSetAllRulesMatch={setAllRulesMatch}
            />
            <div className={styles.onBoardingPage__footer}>
              <p>After you update your password you will be asked to login using your <strong>email address</strong> and the <strong>password</strong> you just set.</p>
              <Toggle id="isAcceptingTerms" name="isAcceptingTerms" checked={info?.isAcceptingTerms || false} isYesNo onChange={_onChange}>
                Do you accept the <a href="/terms/agent" target="_blank">terms of service</a>?
              </Toggle>
            </div>
            <div className={styles.onBoardingPage__formActions}>
              <ButtonPrimary fullWidth color="navy" icon={faCheckCircle} onClick={_onSave} disabled={_isDisabled()}>Continue</ButtonPrimary>
            </div>
          </div>
        )}
        {isLoading && <Loading />}
      </div>
    </BodyClassName>
  )
}

export default PasswordReset